import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, App, Button, List, Typography } from 'antd';

import { AppDispatch, RootState } from '../../store';
import { NCRoamAgencySubscription, NCRoamSubscribeResponse } from './models';

import * as actions from './store/actions';

const { Text } = Typography;

export type NCRoamPricingPlansComponentProps = {
  subscriptionInfo: NCRoamAgencySubscription | null;
  refresh: Function;
};

const NCRoamPricingPlansComponent = ({ subscriptionInfo, refresh }: NCRoamPricingPlansComponentProps) => {
  const { modal } = App.useApp();

  const isInitialized = useRef(false);

  const dispatch = useDispatch<AppDispatch>();

  const { ncRoamSubscriptionPlans, ncRoamSubscriptionPlansLoading } = useSelector((state: RootState) => state.ncRoamSubs);

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      dispatch(actions.getNCRoamSubscriptionPlans());
    }
  }, []);

  const handleSubscribeActionClick = (planId: string) => {
    modal.confirm({
      title: 'Notice',
      icon: null,
      content: (
        <div>
          <>
            <span
              style={{
                display: 'list-item',
                listStyleType: 'circle',
                marginLeft: '1.3em',
              }}
            >
              Payments are processed through Stripe.
            </span>
            <span
              style={{
                display: 'list-item',
                listStyleType: 'circle',
                marginLeft: '1.3em',
              }}
            >
              A subscription will be initiated with a 14-day trial period and Stripe customer profile will be created upon confirmation.
            </span>
            <span
              style={{
                display: 'list-item',
                listStyleType: 'circle',
                marginLeft: '1.3em',
              }}
            >
              You will be redirected to the Stripe checkout page to finalize your subscription.
            </span>
          </>
        </div>
      ),
      okText: 'Confirm',
      onOk() {
        return new Promise((resolve, reject) => {
          dispatch<Promise<NCRoamSubscribeResponse>>(actions.ncRoamSubscribe(planId)).then((res) => {
            resolve(null);

            if (res.data.isInTrial) {
              refresh();
            } else {
              window.location.href = res.data.checkoutSessionUrl;
            }
          });
        });
      },
    });
  };

  const handleUpgradeActionClick = (planId: string) => {
    modal.confirm({
      title: 'Notice',
      icon: null,
      content: (
        <div>
          <span
            style={{
              display: 'list-item',
              listStyleType: 'circle',
              marginLeft: '1.3em',
            }}
          >
            Your subscription will be updated. Payments will be prorated automatically, and the adjusted amount will be charged at the start of your
            next billing cycle.
          </span>
        </div>
      ),
      okText: 'Confirm',
      onOk() {
        return new Promise((resolve, reject) => {
          dispatch(actions.ncRoamUpgradeSubscription(planId)).then(() => {
            resolve(null);

            refresh();
          });
        });
      },
    });
  };

  return (
    <div
      style={{
        maxWidth: 'fit-content',
        minWidth: '550px',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      {subscriptionInfo && subscriptionInfo.exceedsPlanSize ? (
        <Alert
          style={{ marginBottom: '25px', maxWidth: '550px' }}
          message="Plan limits exceeded"
          description="You have reached the maximum number of active users allowed in your current plan. Please consider upgrading to a larger plan that better suits your needs."
          type="warning"
          showIcon
        />
      ) : null}

      <List
        bordered
        loading={ncRoamSubscriptionPlansLoading}
        itemLayout="horizontal"
        dataSource={
          ncRoamSubscriptionPlans
            ? ncRoamSubscriptionPlans.map((item: any) => {
              return {
                id: item.id,
                title: `"${item.name}" Plan`,
                minUsers: item.minUsers,
                maxUsers: item.maxUsers,
                description: `${item.minUsers}-${item.maxUsers} User Accounts`,
                price: `$${item.monthlyPrice}`,
                setupFee: `$${item.setupFee}`,
              };
            })
            : []
        }
        renderItem={(item: any) => {
          const actions =
            subscriptionInfo && item.maxUsers >= subscriptionInfo.totalActiveUsers
              ? [
                subscriptionInfo.exceedsPlanSize ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '3px',
                    }}
                  >
                    <Button
                      type="primary"
                      shape="round"
                      style={
                        item.id.toLowerCase() === subscriptionInfo.nextSuitablePlanId.toLowerCase()
                          ? {
                            backgroundColor: '#52c41a',
                            borderColor: '#52c41a',
                            width: '86px',
                            fontWeight: 'bold',
                          }
                          : {
                            width: '86px',
                            fontWeight: 'bold',
                            background: '#2BC8F3',
                            borderColor: '#2BC8F3',
                            boxShadow: 'none',
                          }
                      }
                      onClick={() => handleUpgradeActionClick(item.id)}
                    >
                      Upgrade
                    </Button>

                    {item.id.toLowerCase() === subscriptionInfo.nextSuitablePlanId.toLowerCase() ? <Text type="success">Recommended</Text> : null}
                  </div>
                ) : (
                  <Button
                    type="primary"
                    shape="round"
                    style={{
                      fontWeight: 'bold',
                      background: '#2BC8F3',
                      borderColor: '#2BC8F3',
                      boxShadow: 'none',
                    }}
                    onClick={() => handleSubscribeActionClick(item.id)}
                  >
                    {subscriptionInfo.trialEndsAt || subscriptionInfo.customerExists ? 'Enable' : 'Start Trial'}
                  </Button>
                ),
              ]
              : [<div style={{ padding: '4px 0px' }}>Not suitable</div>];

          return (
            <List.Item actions={actions}>
              <List.Item.Meta title={item.title} description={item.description} />
              <div style={{ width: '170px', fontWeight: '600', display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                <div>{`Monthly fee: ${item.price}`}</div>
              </div>
            </List.Item>
          );
        }}
      />
    </div>
  );
};

export default NCRoamPricingPlansComponent;
